import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { Link as RouterLink, Navigate } from 'react-router-dom';

import { OtpField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { QR_BACKGROUND, QR_FOREGROUND, QR_QUIET_ZONE_MODULES } from '@/features/identity/constants/qr';
import { useMfaChallenge } from '@/features/identity/hooks/useMfaChallenge';
import { useToast } from '@/hooks/useToast';

/**
 * The MFA screen's form — a distinct route, never a modal bolted onto the login form
 * (docs/development/FrontendGuidelines.md §3). Renders two modes off the same code field: a plain code challenge
 * for an enrolled account, or (BR-2) inline first-time enrollment for a mandatory-MFA account that
 * has never set up TOTP — showing the pending secret to scan, so it can enroll and sign in in one
 * step instead of dead-ending. Never landed on directly: it depends on router state from the login flow.
 */
export function MfaChallengeForm() {
  const { form, onSubmit, serverError, enrollment, isEnrollment, hasChallengeContext } =
    useMfaChallenge();
  const { control, formState } = form;
  const { showSuccess } = useToast();

  if (!hasChallengeContext) {
    return <Navigate to={ROUTES.login} replace />;
  }

  const copySecret = () => {
    if (enrollment) {
      void navigator.clipboard.writeText(enrollment.secret);
      showSuccess('Secret copied.');
    }
  };

  return (
    <AuthCard>
      <AuthHeader
        title={isEnrollment ? 'Set up multi-factor authentication' : 'Enter verification code'}
        subtitle={
          isEnrollment
            ? 'Your role requires MFA. Scan the code with your authenticator app, then enter the 6-digit code to finish.'
            : 'Open your authenticator app for the current code.'
        }
      />
      <Stack component="form" onSubmit={onSubmit} spacing={2} noValidate alignItems="center">
        {serverError ? (
          <Alert severity="error" sx={{ width: '100%' }}>
            {serverError}
          </Alert>
        ) : null}

        {isEnrollment && enrollment ? (
          <>
            <QRCodeSVG
              value={enrollment.provisioningUri}
              size={180}
              marginSize={QR_QUIET_ZONE_MODULES}
              bgColor={QR_BACKGROUND}
              fgColor={QR_FOREGROUND}
            />
            <Typography variant="caption" color="text.secondary">
              Or enter this code manually:
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <TextField
                value={enrollment.secret}
                slotProps={{ input: { readOnly: true } }}
                fullWidth
                size="small"
              />
              <Tooltip title="Copy secret">
                <IconButton onClick={copySecret} aria-label="Copy secret">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </>
        ) : null}

        <Typography variant="caption" color="text.secondary">
          Verification code
        </Typography>
        <OtpField name="mfaCode" control={control} />
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={formState.isSubmitting}
          startIcon={
            formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {isEnrollment ? 'Verify & enable MFA' : 'Verify'}
        </Button>

        {/* Enrollment mode means this account has never set up MFA — there is nothing to recover
            from yet, so the link only makes sense once a real authenticator exists to lose. */}
        {isEnrollment ? null : (
          <Link component={RouterLink} to={ROUTES.forgotMfa} variant="body2">
            Lost your authenticator?
          </Link>
        )}
      </Stack>
    </AuthCard>
  );
}
